import {
    Box,
    Flex,
    Heading,
    Text,
    Badge,
    Button,
    HStack,
    Divider,
    Spinner,
    useDisclosure,
    useColorModeValue,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from '@chakra-ui/react';
import { MdArrowBack } from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom';
import { usePursuit } from '../services/hooks';
import { PDFPreviewModal } from '../components/ImportedItems/PDFPreviewModal';
import { useState } from 'react';

// New Components
import { OverviewTab } from '../components/Pursuits/OverviewTab';
import { AnalysisTab } from '../components/Pursuits/AnalysisTab';
import { BidPrepTab } from '../components/Pursuits/BidPrepTab';
import { BidSubmittalsTab } from '../components/Pursuits/BidSubmittalsTab';
import { FinalSubmittalsTab } from '../components/Pursuits/FinalSubmittalsTab';

export function BidRoom() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: pursuit, isLoading } = usePursuit(Number(id));

    // PDF Preview State
    const [previewPage, setPreviewPage] = useState<number | undefined>(undefined);
    const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();

    // Theme-aware colors
    const headerBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const mutedText = useColorModeValue('gray.500', 'gray.400');

    const handleOpenPreview = (page?: number) => {
        setPreviewPage(page);
        onPreviewOpen();
    };

    if (isLoading) {
        return (
            <Flex justify="center" align="center" h="60vh">
                <Spinner size="xl" color="brand.500" thickness="3px" />
            </Flex>
        );
    }
    if (!pursuit) {
        return (
            <Flex justify="center" align="center" h="60vh" direction="column" gap={3}>
                <Text fontSize="lg" fontWeight="semibold">Pursuit not found</Text>
                <Button variant="outline" onClick={() => navigate('/pursuits')}>Back to Pursuits</Button>
            </Flex>
        );
    }

    const item = pursuit.imported_item;
    if (!item) {
        return (
            <Flex justify="center" align="center" h="60vh" direction="column" gap={3}>
                <Text fontSize="lg" fontWeight="semibold">No bid data linked to this pursuit.</Text>
                <Button variant="outline" onClick={() => navigate('/pursuits')}>Back to Pursuits</Button>
            </Flex>
        );
    }

    return (
        <Flex
            h={{ base: 'auto', md: "calc(100vh - 80px)" }}
            direction="column"
            gap={{ base: 2, md: 4 }}
        >
            {/* Header */}
            <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'flex-start', md: 'center' }}
                bg={headerBg}
                p={{ base: 3, md: 4 }}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow="sm"
                gap={{ base: 3, md: 0 }}
            >
                <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} gap={2}>
                    <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<MdArrowBack />}
                        onClick={() => navigate('/pursuits')}
                        color={mutedText}
                        px={0}
                    >
                        Pursuits
                    </Button>
                    <Divider
                        orientation="vertical"
                        h="30px"
                        display={{ base: 'none', md: 'block' }}
                    />
                    <Divider
                        orientation="horizontal"
                        display={{ base: 'block', md: 'none' }}
                        borderColor={borderColor}
                    />
                    <Box>
                        <HStack mb={1} spacing={2} wrap="wrap">
                            <Badge
                                colorScheme={pursuit.stage === 'won' ? 'green' : pursuit.stage === 'lost' ? 'red' : 'blue'}
                                fontSize="2xs"
                                borderRadius="full"
                                px={2}
                            >
                                {pursuit.stage?.toUpperCase() || 'REVIEWING'}
                            </Badge>
                            <Badge
                                colorScheme={item.opportunity_type === 'active' ? 'green' : 'gray'}
                                fontSize="2xs"
                                borderRadius="full"
                                px={2}
                                variant="outline"
                            >
                                {item.opportunity_type?.toUpperCase()}
                            </Badge>
                        </HStack>
                        <Heading size="sm" lineHeight="short" maxW={{ base: '100%', md: '800px' }} noOfLines={1}>
                            {item.project_title || item.description?.substring(0, 60)}
                        </Heading>
                    </Box>
                </Flex>
            </Flex>

            {/* Tabs Interface */}
            <Tabs
                variant="enclosed"
                colorScheme="brand"
                isLazy
                display="flex"
                flexDirection="column"
                flex={1}
                overflow="hidden"
                size={{ base: 'sm', md: 'md' }}
            >
                <TabList
                    px={2}
                    overflowX="auto"
                    overflowY="hidden"
                    sx={{
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        whiteSpace: 'nowrap'
                    }}
                >
                    <Tab fontWeight="medium">Overview</Tab>
                    <Tab fontWeight="medium">AI Analysis</Tab>
                    <Tab fontWeight="medium">Bid Prep</Tab>
                    <Tab fontWeight="medium">Bid Submittals</Tab>
                    <Tab fontWeight="medium">Final Submittals</Tab>
                </TabList>

                <TabPanels flex={1} overflowY="auto" pb={4}>
                    {/* Overview Tab */}
                    <TabPanel h="100%">
                        <OverviewTab
                            item={item}
                            onOpenPreview={handleOpenPreview}
                        />
                    </TabPanel>

                    {/* AI Analysis Tab */}
                    <TabPanel h="100%" p={0} pt={4}>
                        <AnalysisTab
                            item={item}
                            onOpenPreview={handleOpenPreview}
                        />
                    </TabPanel>

                    {/* Bid Prep Tab */}
                    <TabPanel h="100%">
                        {/* We pass pursuitId for compliance linkage */}
                        <BidPrepTab
                            pursuitId={pursuit.id}
                            importedItemId={item.id}
                        />
                    </TabPanel>

                    {/* Bid Submittals Tab */}
                    <TabPanel h="100%">
                        <BidSubmittalsTab pursuitId={pursuit.id} />
                    </TabPanel>

                    {/* Final Submittals Tab */}
                    <TabPanel h="100%">
                        <FinalSubmittalsTab pursuitId={pursuit.id} />
                    </TabPanel>
                </TabPanels>
            </Tabs>

            {/* PDF Modal (Global for page) */}
            <PDFPreviewModal
                isOpen={isPreviewOpen}
                onClose={() => { onPreviewClose(); setPreviewPage(undefined); }}
                pdfUrl={`${import.meta.env.VITE_API_URL || '/api'}/v1/documents/bid/${item.id}/pdf`}
                title={`${item.item_code || 'Document'} - Review`}
                page={previewPage}
            />
        </Flex>
    );
}
